import * as React from 'react'
import {
  AlertCircle as WarningCircleIcon,
  BookmarkPlus as SaveDatapointIcon,
  Play as PlayIcon,
  Table as TableIcon
} from 'lucide-react'
import {
  isDuckDbPlantDatabase,
  isManagedPlantDatabase,
  useExecutePlantDbReadQueryMutation,
  useExecutePlantDbWriteQueryMutation,
  useGetPlantDbSchemaQuery,
  useListDbConnectionsQuery,
  useRegisterDbConnectionMutation,
  type DbConnectionSummary,
  type PlantComputeHost,
  type PlantDatabase,
  type PlantDbExecuteArgs,
  type PlantDbSchemaArgs,
  type PlantDbTable,
  type RegisterDbDatapointConnection
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { cn } from '../../lib/utils'
import { designPixelsToRem, screenPixelsToDesignPixels } from '../../lib/ui-density'
import { computeTypeKey } from './compute-icon'
import { SaveAsDatapointDialog } from './SaveAsDatapointDialog'

const RESULT_DISPLAY_CAP = 1_000
const QUERY_PERSIST_DEBOUNCE_MS = 500
const MAX_PERSISTED_QUERY_LEN = 50_000
const RAIL_WIDTH_KEY = 'ontology.dbQuery.railWidth'
const RAIL_WIDTH_DEFAULT = 240
const RAIL_WIDTH_MIN = 160
const RAIL_WIDTH_MAX = 480

/**
 * Inline query workbench for a single `Database`. Drilldown opened
 * from `ComputeHostDetailPage` when `?db=<id>` is set.
 *
 * Schema and execute always address a **registered** connection by graph
 * `:Database` URN (`connectionId` → `GET/POST …/db/connections/{id}/…`).
 * Transport (webdev vs direct) is resolved server-side from posture.
 *
 * Direct Postgres still uses a connect-once card (AXI-4467): password in
 * the proxy secret store. Ignition/webdev posture is usually seeded at
 * gwbk ingest; if missing, a one-click register stamps `database_id`.
 */
export function DatabaseQueryEngine({
  host,
  databases,
  activeDatabaseId,
  onActiveDatabaseChange
}: {
  host: PlantComputeHost
  databases: PlantDatabase[]
  activeDatabaseId: string
  onActiveDatabaseChange: (id: string) => void
}): React.ReactElement | null {
  const activeDatabase = databases.find((db) => db.id === activeDatabaseId) ?? databases[0] ?? null

  if (!activeDatabase) return null

  return (
    <section className="mb-card overflow-hidden rounded-xl border bg-card text-card-foreground shadow-sm">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line px-5 py-3.5">
        <span className="text-10 font-semibold uppercase tracking-[0.1em] text-ink-4">
          Database
        </span>
        <h3 className="m-0 font-mono text-sm font-medium text-ink">{activeDatabase.name}</h3>
        {activeDatabase.database_type && (
          <Badge variant="outline" className="ml-1">
            {activeDatabase.database_type}
          </Badge>
        )}
      </header>

      {databases.length > 1 && (
        <DbTabsStrip
          databases={databases}
          activeId={activeDatabase.id}
          onChange={onActiveDatabaseChange}
        />
      )}

      <EngineBody key={`${host.id}:${activeDatabase.id}`} host={host} database={activeDatabase} />
    </section>
  )
}

function DbTabsStrip({
  databases,
  activeId,
  onChange
}: {
  databases: PlantDatabase[]
  activeId: string
  onChange: (id: string) => void
}): React.ReactElement {
  return (
    <div
      role="tablist"
      aria-label="Databases"
      className="flex flex-wrap gap-1 border-b border-line bg-bg-sunk/30 px-4 py-2"
    >
      {databases.map((db) => {
        const active = db.id === activeId
        return (
          <button
            key={db.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(db.id)}
            className={cn(
              'cursor-pointer rounded-md border px-3 py-1 text-12 font-mono transition-colors',
              active
                ? 'border-brand bg-brand-soft text-brand'
                : 'border-line text-ink-2 hover:bg-bg-sunk hover:text-ink focus-visible:border-brand focus-visible:outline-none'
            )}
          >
            {db.name}
            {db.database_type && (
              <span className="ml-2 text-10 text-ink-4">{db.database_type}</span>
            )}
          </button>
        )
      })}
    </div>
  )
}

function EngineBody({
  host,
  database
}: {
  host: PlantComputeHost
  database: PlantDatabase
}): React.ReactElement {
  const isIgnitionHost = computeTypeKey(host.compute_type) === 'ignition'
  // Posture match is by graph URN (`connection_id`), never host shape / URL.
  const connectionsQuery = useListDbConnectionsQuery()
  const registeredConnection = React.useMemo(() => {
    return connectionsQuery.data?.find((c) => c.connectionId === database.id)
  }, [database.id, connectionsQuery.data])

  // Dumps (`transport=duckdb`) and proxy-managed mirrors (`transport=managed`,
  // e.g. a PSS mirror) are registered by their ingest and read-only: no
  // connect card, no write mode, no datapoint registration.
  const isDump =
    registeredConnection?.transport === 'duckdb' ||
    registeredConnection?.transport === 'managed' ||
    isDuckDbPlantDatabase(database) ||
    isManagedPlantDatabase(database)

  const isRegistered = Boolean(registeredConnection)
  const ready = isRegistered
  const schemaArgs: PlantDbSchemaArgs = { connectionId: database.id }

  const schemaQuery = useGetPlantDbSchemaQuery(schemaArgs, { skip: !ready })
  const tables = schemaQuery.data?.tables ?? []

  const persistKey = `ontology.dbQuery.${host.id}.${database.id}`
  const [query, setQuery] = React.useState<string>(() => loadPersistedQuery(persistKey))
  React.useEffect(() => {
    const id = window.setTimeout(() => persistQuery(persistKey, query), QUERY_PERSIST_DEBOUNCE_MS)
    return () => window.clearTimeout(id)
  }, [persistKey, query])

  const [writeMode, setWriteMode] = React.useState(false)
  // Read vs write share the connection-addressed `/query` URL; `query_type`
  // selects the mode. Separate mutations keep RTK result state independent.
  const [executeReadQuery, readResult] = useExecutePlantDbReadQueryMutation()
  const [executeWriteQuery, writeResult] = useExecutePlantDbWriteQueryMutation()
  const executeResult = writeMode && !isDump ? writeResult : readResult

  // "Save as data point" is only meaningful for a successful READ result — a
  // scalar/column that can be polled on cadence. Pull the columns off the last
  // read so the dialog can offer them as the value/time column pickers.
  const [saveOpen, setSaveOpen] = React.useState(false)
  const lastRead = !writeMode && readResult.data ? parseDbResult(readResult.data.result) : null
  const savableColumns = lastRead && lastRead.kind === 'read' ? lastRead.columns : []
  // Posture refresh for datapoint registration still uses URL+transport; the
  // connection is already registered by the time a query can run.
  const saveConnection: RegisterDbDatapointConnection = isIgnitionHost
    ? {
        url: host.url ?? '',
        name: database.name,
        transport: 'webdev',
        ...(database.database_type ? { databaseType: database.database_type } : {})
      }
    : {
        url: database.url ?? '',
        name: database.name,
        transport: 'direct',
        databaseType: 'POSTGRESQL'
      }
  const canSaveDatapoint = savableColumns.length > 0 && query.trim().length > 0 && !isDump

  // Wait for a successful schema probe so Run can't fire against an
  // unverified registered target (esp. write mode).
  const canRun =
    query.trim().length > 0 && ready && !executeResult.isLoading && schemaQuery.isSuccess

  const handleRun = React.useCallback(() => {
    if (query.trim().length === 0 || !ready) return
    const baseArgs: PlantDbExecuteArgs = { connectionId: database.id, query }
    if (writeMode && !isDump) void executeWriteQuery(baseArgs)
    else void executeReadQuery(baseArgs)
  }, [query, ready, database.id, executeReadQuery, executeWriteQuery, writeMode, isDump])

  const previewDatabaseType = database.database_type ?? registeredConnection?.databaseType
  const handlePickTable = React.useCallback(
    (table: PlantDbTable) => {
      setQuery(defaultQueryForTable(table, previewDatabaseType))
    },
    [previewDatabaseType]
  )

  const railIsError = ready && schemaQuery.isError
  const railIsNotReady = !ready || schemaQuery.isError
  // Direct always shows the credentials card; webdev only when posture is
  // missing (gwbk usually seeds it). Dumps are already registered
  // `transport=duckdb` and are read-only — no connect card.
  const showConnectionCard = !isDump && (!isIgnitionHost || !isRegistered)

  const [railWidth, setRailWidth] = React.useState<number>(loadRailWidth)
  const dragStateRef = React.useRef<{ startX: number; startWidth: number } | null>(null)

  React.useEffect(() => {
    const onMove = (event: MouseEvent): void => {
      const drag = dragStateRef.current
      if (!drag) return
      const next = Math.min(
        RAIL_WIDTH_MAX,
        Math.max(
          RAIL_WIDTH_MIN,
          drag.startWidth + screenPixelsToDesignPixels(event.clientX - drag.startX)
        )
      )
      setRailWidth(next)
    }
    const onUp = (): void => {
      if (!dragStateRef.current) return
      dragStateRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [])

  React.useEffect(() => {
    const id = window.setTimeout(() => persistRailWidth(railWidth), 200)
    return () => window.clearTimeout(id)
  }, [railWidth])

  const handleDividerMouseDown = (event: React.MouseEvent): void => {
    dragStateRef.current = { startX: event.clientX, startWidth: railWidth }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    event.preventDefault()
  }

  return (
    <div className="flex flex-col">
      {showConnectionCard && (
        <RegisteredConnectionCard
          host={host}
          database={database}
          transport={isIgnitionHost ? 'webdev' : 'direct'}
          registeredConnection={registeredConnection}
          isListing={connectionsQuery.isLoading}
          schemaError={
            isRegistered && schemaQuery.isError ? getErrorMessage(schemaQuery.error) : null
          }
        />
      )}
      <div className="flex flex-col md:flex-row">
        <div className="md:shrink-0" style={{ width: designPixelsToRem(railWidth) }}>
          <TableRail
            tables={tables}
            isLoading={schemaQuery.isLoading || schemaQuery.isFetching}
            isError={railIsError}
            notReady={railIsNotReady}
            onPick={handlePickTable}
          />
        </div>
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize table list"
          onMouseDown={handleDividerMouseDown}
          onDoubleClick={() => setRailWidth(RAIL_WIDTH_DEFAULT)}
          className="hidden w-1 shrink-0 cursor-col-resize bg-line transition-colors hover:bg-brand md:block"
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <QueryToolbar
            writeMode={writeMode}
            onWriteModeChange={setWriteMode}
            allowWrite={!isDump}
            onRun={handleRun}
            canRun={canRun}
            isRunning={executeResult.isLoading}
          />
          <textarea
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault()
                handleRun()
              }
            }}
            rows={5}
            spellCheck={false}
            placeholder={
              isDump
                ? 'Pick a table on the left to start, or write SQL directly. ⌘↵ to run.'
                : isRegistered
                  ? 'Pick a table on the left to start, or write SQL directly. ⌘↵ to run.'
                  : 'Register the database connection above to start querying.'
            }
            className="resize-y border-t border-line bg-bg-sunk px-4 py-3 font-mono text-13 leading-relaxed text-ink outline-none placeholder:text-ink-4 focus-visible:bg-card"
          />
          <ResultsPane
            mutation={executeResult}
            writeMode={writeMode}
            onSaveAsDatapoint={canSaveDatapoint ? () => setSaveOpen(true) : undefined}
          />
        </div>
      </div>
      <SaveAsDatapointDialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        connection={saveConnection}
        query={query}
        columns={savableColumns}
      />
    </div>
  )
}

/**
 * Connection posture card (AXI-4467 / P5).
 *
 * Direct: connect-once username/password; password stays in the proxy secret
 * store. Webdev: one-click register when gwbk posture is missing (no password).
 * Both stamp `database_id` = graph `:Database` URN so later schema/query
 * address the same identity.
 */
function RegisteredConnectionCard({
  host,
  database,
  transport,
  registeredConnection,
  isListing,
  schemaError
}: {
  host: PlantComputeHost
  database: PlantDatabase
  transport: 'webdev' | 'direct'
  registeredConnection: DbConnectionSummary | undefined
  isListing: boolean
  schemaError: string | null
}): React.ReactElement {
  const [editing, setEditing] = React.useState(false)
  const [draftUsername, setDraftUsername] = React.useState<string>('')
  const [draftPassword, setDraftPassword] = React.useState<string>('')
  const [register, registerState] = useRegisterDbConnectionMutation()

  const registerUrl = transport === 'webdev' ? (host.url ?? '') : (database.url ?? '')
  const target = registerUrl || database.name
  const trimmedUsername = draftUsername.trim()
  const canConnectDirect =
    transport === 'direct' &&
    trimmedUsername.length > 0 &&
    registerUrl.length > 0 &&
    !registerState.isLoading
  const canConnectWebdev =
    transport === 'webdev' && registerUrl.length > 0 && !registerState.isLoading

  const submit = (): void => {
    if (transport === 'webdev') {
      if (!canConnectWebdev) return
      void register({
        url: registerUrl,
        name: database.name,
        databaseId: database.id,
        transport: 'webdev',
        ...(database.database_type ? { databaseType: database.database_type } : {})
      }).then(
        () => {
          setEditing(false)
        },
        () => {
          // Error surfaced via registerState.error below.
        }
      )
      return
    }
    if (!canConnectDirect) return
    void register({
      url: registerUrl,
      name: database.name,
      databaseId: database.id,
      transport: 'direct',
      databaseType: 'POSTGRESQL',
      username: trimmedUsername,
      ...(draftPassword ? { password: draftPassword } : {})
    }).then(
      () => {
        setEditing(false)
        setDraftPassword('')
      },
      () => {
        // Error surfaced via registerState.error below; keep the form open.
      }
    )
  }

  // Connected state: a registered connection and not mid-edit.
  if (registeredConnection && !editing) {
    return (
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-bg-sunk/30 px-4 py-2 text-xs">
        <span className="size-1.5 rounded-full bg-st-run" aria-hidden />
        <span className="text-ink-3">
          {transport === 'direct' ? (
            <>
              Connected as{' '}
              <span className="font-mono text-ink-2">{registeredConnection.username || '—'}</span>
              <span className="ml-1 text-ink-4">(registered)</span>
            </>
          ) : (
            <span className="text-ink-4">(registered)</span>
          )}
        </span>
        <span className="font-mono text-11 text-ink-4">→ {target}</span>
        {transport === 'direct' && (
          <button
            type="button"
            onClick={() => {
              setDraftUsername(registeredConnection.username ?? '')
              setDraftPassword('')
              setEditing(true)
            }}
            className="ml-auto cursor-pointer text-ink-3 hover:text-ink hover:underline"
          >
            Update credentials
          </button>
        )}
        {schemaError && (
          <div className="w-full text-12 text-st-warn">
            Connected, but the schema probe failed: <span className="font-mono">{schemaError}</span>
          </div>
        )}
      </div>
    )
  }

  const missingUrl = !registerUrl

  if (transport === 'webdev') {
    return (
      <div className="border-b border-line bg-bg-sunk/30 px-4 py-3">
        <div className="mb-2 flex items-baseline gap-2">
          <span className="text-10 font-semibold uppercase tracking-wider text-ink-3">
            Register database connection
          </span>
          <span className="font-mono text-11 text-ink-4">→ {target}</span>
          {isListing && <span className="text-11 text-ink-4">checking…</span>}
        </div>
        {missingUrl ? (
          <div className="text-12 text-st-warn">
            This Ignition host has no gateway URL, so the database can&apos;t be registered.
          </div>
        ) : (
          <>
            <p className="mb-2 max-w-prose text-12 leading-relaxed text-ink-4">
              Register this datasource on the gateway so schema and queries can run. No database
              password is stored — Ignition WebDev relays the query.
            </p>
            <button
              type="button"
              disabled={!canConnectWebdev}
              onClick={submit}
              className="cursor-pointer rounded border border-brand bg-brand-soft px-3 py-1 text-12 font-medium text-brand disabled:cursor-not-allowed disabled:opacity-50"
            >
              {registerState.isLoading ? 'Registering…' : 'Register connection'}
            </button>
            {registerState.error && (
              <div className="mt-2 text-12 text-st-warn">
                {getErrorMessage(registerState.error)}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  return (
    <div className="border-b border-line bg-bg-sunk/30 px-4 py-3">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-10 font-semibold uppercase tracking-wider text-ink-3">
          {registeredConnection ? 'Update database credentials' : 'Connect database'}
        </span>
        <span className="font-mono text-11 text-ink-4">→ {target}</span>
        {isListing && <span className="text-11 text-ink-4">checking…</span>}
      </div>
      {missingUrl ? (
        <div className="text-12 text-st-warn">
          This database has no connection URL, so it can&apos;t be registered.
        </div>
      ) : (
        <>
          <p className="mb-2 max-w-prose text-12 leading-relaxed text-ink-4">
            Connect once. The password is stored server-side in the secret store — it is never kept
            in the browser and isn&apos;t sent again after connecting.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-10 uppercase tracking-wider text-ink-4">Username</span>
              <input
                type="text"
                autoComplete="off"
                value={draftUsername}
                onChange={(event) => setDraftUsername(event.target.value)}
                placeholder="postgres"
                className="rounded border border-line bg-card px-2 py-1 font-mono text-13 text-ink outline-none focus-visible:border-brand"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-10 uppercase tracking-wider text-ink-4">Password</span>
              <input
                type="password"
                autoComplete="off"
                value={draftPassword}
                onChange={(event) => setDraftPassword(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    submit()
                  }
                }}
                className="rounded border border-line bg-card px-2 py-1 font-mono text-13 text-ink outline-none focus-visible:border-brand"
              />
            </label>
            <button
              type="button"
              onClick={submit}
              disabled={!canConnectDirect}
              className={cn(
                'rounded-md border px-3 py-1 text-xs font-medium transition-colors',
                !canConnectDirect
                  ? 'cursor-not-allowed border-line bg-card text-ink-4'
                  : 'cursor-pointer border-brand bg-brand text-brand-fg hover:opacity-90'
              )}
            >
              {registerState.isLoading ? 'Connecting…' : 'Connect'}
            </button>
            {registeredConnection && (
              <button
                type="button"
                onClick={() => {
                  setEditing(false)
                  setDraftPassword('')
                }}
                className="cursor-pointer px-2 py-1 text-xs text-ink-3 hover:text-ink hover:underline"
              >
                Cancel
              </button>
            )}
          </div>
          {registerState.error && (
            <div className="mt-2 text-12 text-st-warn">
              Couldn&apos;t connect: <span className="font-mono">{registerState.error}</span>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function TableRail({
  tables,
  isLoading,
  isError,
  notReady,
  onPick
}: {
  tables: PlantDbTable[]
  isLoading: boolean
  isError: boolean
  notReady: boolean
  onPick: (table: PlantDbTable) => void
}): React.ReactElement {
  if (notReady) {
    return <div className="p-4 text-sm text-ink-4">Register a connection to see tables.</div>
  }
  if (isError) {
    return (
      <div className="p-4 text-sm text-st-warn">
        Couldn&apos;t load schema. The registered connection may be unreachable.
      </div>
    )
  }
  if (isLoading) {
    return <div className="p-4 text-sm text-ink-3">Loading schema…</div>
  }
  if (tables.length === 0) {
    return <div className="p-4 text-sm text-ink-3">No tables visible on this connection.</div>
  }
  const grouped = groupTablesBySchema(tables)
  return (
    <div className="max-h-[32rem] overflow-y-auto">
      <div className="sticky top-0 z-10 border-b border-line bg-card px-3 pb-2 pt-3 text-10 font-semibold uppercase tracking-[0.1em] text-ink-4">
        Tables
      </div>
      {grouped.map((section) => (
        <div key={section.schema ?? '__unscoped__'} className="px-1.5 pb-2 pt-1.5">
          {section.schema && (
            <div className="px-2 pb-1 pt-1 font-mono text-10 uppercase tracking-wider text-ink-4">
              {section.schema}
            </div>
          )}
          {section.tables.map((table) => (
            <button
              key={`${section.schema ?? ''}.${table.name}`}
              type="button"
              onClick={() => onPick(table)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-12 transition-colors hover:bg-bg-sunk focus-visible:bg-bg-sunk focus-visible:outline-none"
            >
              <TableIcon size={12} className="shrink-0 text-ink-4" aria-hidden />
              <span className="truncate font-mono text-ink-2">{table.name}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  )
}

function QueryToolbar({
  writeMode,
  onWriteModeChange,
  allowWrite = true,
  onRun,
  canRun,
  isRunning
}: {
  writeMode: boolean
  onWriteModeChange: (next: boolean) => void
  allowWrite?: boolean
  onRun: () => void
  canRun: boolean
  isRunning: boolean
}): React.ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-2">
      <button
        type="button"
        onClick={onRun}
        disabled={!canRun}
        className={cn(
          'flex items-center gap-1.5 rounded-md border px-3 py-1 text-xs font-medium transition-colors',
          !canRun
            ? 'cursor-not-allowed border-line bg-card text-ink-4'
            : writeMode
              ? 'cursor-pointer border-st-warn bg-st-warn-bg text-st-warn hover:opacity-90 focus-visible:outline-none'
              : 'cursor-pointer border-brand bg-brand text-brand-fg hover:opacity-90 focus-visible:outline-none'
        )}
      >
        {/* Phosphor `weight="fill"` → lucide has no fill variant; use `fill="currentColor"`
            so the play triangle reads as solid against the button background. */}
        <PlayIcon size={12} fill="currentColor" />
        {isRunning ? 'Running…' : 'Run'}
      </button>
      {allowWrite && (
        <label className="flex cursor-pointer select-none items-center gap-1.5 text-11 text-ink-3">
          <input
            type="checkbox"
            checked={writeMode}
            onChange={(event) => onWriteModeChange(event.target.checked)}
            className="size-3 cursor-pointer accent-st-warn"
          />
          Write mode
        </label>
      )}
      {allowWrite && writeMode && (
        <span className="text-11 text-st-warn">
          DDL/DML statements will execute against the database.
        </span>
      )}
      <span className="ml-auto whitespace-nowrap text-10 uppercase tracking-[0.06em] text-ink-4">
        ⌘↵ to run
      </span>
    </div>
  )
}

function ResultsPane({
  mutation,
  writeMode,
  onSaveAsDatapoint
}: {
  mutation: ReturnType<typeof useExecutePlantDbReadQueryMutation>[1]
  writeMode: boolean
  onSaveAsDatapoint?: () => void
}): React.ReactElement | null {
  if (mutation.isLoading) {
    return <div className="border-t border-line px-4 py-4 text-sm text-ink-3">Running query…</div>
  }
  if (mutation.isError) {
    return (
      <div className="border-t border-line p-3">
        <Empty
          title="Query failed"
          hint={getErrorMessage(mutation.error)}
          icon={WarningCircleIcon}
        />
      </div>
    )
  }
  if (mutation.isUninitialized) {
    return (
      <div className="border-t border-line px-4 py-4 text-sm text-ink-4">
        {writeMode
          ? 'Run a query to see how many rows are affected. ⌘↵ or click Run.'
          : 'Run a query to see results. ⌘↵ or click Run.'}
      </div>
    )
  }
  if (!mutation.data) return null
  const parsed = parseDbResult(mutation.data.result)
  return (
    <div className="border-t border-line">
      <ResultsBody parsed={parsed} onSaveAsDatapoint={onSaveAsDatapoint} />
    </div>
  )
}

function ResultsBody({
  parsed,
  onSaveAsDatapoint
}: {
  parsed: ParsedDbResult
  onSaveAsDatapoint?: () => void
}): React.ReactElement {
  if (parsed.kind === 'write') {
    return (
      <div className="px-4 py-4 text-sm text-ink-2">
        <span className="font-mono text-brand">{parsed.updateCount.toLocaleString()}</span>{' '}
        {parsed.updateCount === 1 ? 'row' : 'rows'} affected.
      </div>
    )
  }
  if (parsed.kind === 'unknown') {
    return (
      <div className="px-4 py-3">
        <div className="mb-1 text-10 uppercase tracking-wider text-ink-4">Raw response</div>
        <pre className="max-h-80 overflow-auto rounded-md border border-line bg-bg-sunk p-2 font-mono text-12 text-ink-2">
          {safeStringify(parsed.raw)}
        </pre>
      </div>
    )
  }
  if (parsed.rows.length === 0) {
    return <div className="px-4 py-4 text-sm text-ink-3">Query returned 0 rows.</div>
  }
  const truncated = parsed.rows.length > RESULT_DISPLAY_CAP
  const displayRows = truncated ? parsed.rows.slice(0, RESULT_DISPLAY_CAP) : parsed.rows
  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between gap-2 px-4 pb-2 pt-3 text-11 text-ink-3">
        <span>
          <span className="font-medium text-ink-2">{parsed.rowCount.toLocaleString()}</span>{' '}
          {parsed.rowCount === 1 ? 'row' : 'rows'}
          {truncated && (
            <>
              {' · '}
              <span className="text-st-warn">
                showing first {RESULT_DISPLAY_CAP.toLocaleString()}
              </span>
            </>
          )}
        </span>
        {onSaveAsDatapoint && parsed.columns.length > 0 && (
          <button
            type="button"
            onClick={onSaveAsDatapoint}
            className="flex cursor-pointer items-center gap-1.5 rounded-md border border-line px-2.5 py-1 text-11 font-medium text-ink-2 transition-colors hover:border-brand hover:bg-brand-soft hover:text-brand focus-visible:border-brand focus-visible:outline-none"
          >
            <SaveDatapointIcon size={13} aria-hidden />
            Save as data point
          </button>
        )}
      </div>
      <div className="min-h-[20rem] max-h-[32rem] overflow-auto border-t border-line">
        <table className="w-full border-collapse text-left text-xs">
          <thead className="sticky top-0 z-10 bg-bg-sunk/95 text-ink-4 backdrop-blur">
            <tr>
              {parsed.columns.map((col) => (
                <th
                  key={col}
                  className="border-b border-line px-3 py-2 text-11 font-semibold uppercase tracking-[0.06em]"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, idx) => (
              <tr key={idx} className="border-b border-line last:border-b-0 hover:bg-bg-sunk/60">
                {parsed.columns.map((col, colIdx) => (
                  <td
                    key={col}
                    className={cn(
                      'whitespace-nowrap px-3 py-1.5 font-mono text-11',
                      typeof row[colIdx] === 'number' ? 'text-brand' : 'text-ink-2'
                    )}
                  >
                    {formatCell(row[colIdx])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

interface TableSection {
  schema: string | undefined
  tables: PlantDbTable[]
}

function groupTablesBySchema(tables: PlantDbTable[]): TableSection[] {
  const map = new Map<string, PlantDbTable[]>()
  const unscoped: PlantDbTable[] = []
  for (const table of tables) {
    const schema = table.schema?.trim()
    if (!schema) {
      unscoped.push(table)
      continue
    }
    const bucket = map.get(schema) ?? []
    bucket.push(table)
    map.set(schema, bucket)
  }
  const sections: TableSection[] = []
  if (unscoped.length > 0) sections.push({ schema: undefined, tables: unscoped })
  for (const [schema, group] of Array.from(map.entries()).sort((a, b) =>
    a[0].localeCompare(b[0])
  )) {
    sections.push({
      schema,
      tables: group.slice().sort((a, b) => a.name.localeCompare(b.name))
    })
  }
  return sections
}

const PREVIEW_ROW_LIMIT = 100

type PreviewDialect = 'mssql' | 'oracle' | 'mysql' | 'default'

// `database_type` is free text: gateway ingest stores the JDBC driver name
// ("Microsoft SQLServer JDBC Driver"), Ignition reports "MSSQL" / "ORACLE",
// and registered connections use short engine names ("mssql", "postgres").
function previewDialect(databaseType: string | undefined): PreviewDialect {
  const type = (databaseType ?? '').toLowerCase().replace(/\s+/g, '')
  if (type.includes('sqlserver') || type.includes('mssql')) return 'mssql'
  if (type.includes('oracle')) return 'oracle'
  if (type.includes('mysql') || type.includes('mariadb')) return 'mysql'
  return 'default'
}

/**
 * Starter query for a table picked from the rail. SQL Server has no `LIMIT`,
 * Oracle needs `ROWNUM` to stay version-agnostic, and MySQL only accepts
 * double-quoted identifiers under `ANSI_QUOTES`.
 */
export function defaultQueryForTable(table: PlantDbTable, databaseType?: string): string {
  const dialect = previewDialect(databaseType)
  const quote =
    dialect === 'mysql'
      ? (id: string) => `\`${id.replace(/`/g, '``')}\``
      : (id: string) => `"${id.replace(/"/g, '""')}"`
  const schema = table.schema?.trim()
  const target = schema ? `${quote(schema)}.${quote(table.name)}` : quote(table.name)
  switch (dialect) {
    case 'mssql':
      return `SELECT TOP ${PREVIEW_ROW_LIMIT} * FROM ${target}`
    case 'oracle':
      return `SELECT * FROM ${target} WHERE ROWNUM <= ${PREVIEW_ROW_LIMIT}`
    default:
      return `SELECT * FROM ${target} LIMIT ${PREVIEW_ROW_LIMIT}`
  }
}

type ParsedDbResult =
  | { kind: 'read'; columns: string[]; rows: unknown[][]; rowCount: number }
  | { kind: 'write'; updateCount: number }
  | { kind: 'unknown'; raw: unknown }

/**
 * Both backends return the same wire shape:
 *   - read  → `Array<Record<string, unknown>>`
 *   - write → `number` (rows affected)
 * Anything else falls through to `unknown` and renders as raw JSON.
 */
function parseDbResult(value: unknown): ParsedDbResult {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { kind: 'write', updateCount: Math.max(0, Math.trunc(value)) }
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return { kind: 'read', columns: [], rows: [], rowCount: 0 }
    }
    const firstRow = value[0]
    if (!firstRow || typeof firstRow !== 'object') {
      return { kind: 'unknown', raw: value }
    }
    const columns = Object.keys(firstRow as Record<string, unknown>)
    const rows = value.map((row) => {
      if (!row || typeof row !== 'object') return columns.map(() => null)
      const obj = row as Record<string, unknown>
      return columns.map((col) => obj[col])
    })
    return { kind: 'read', columns, rows, rowCount: rows.length }
  }
  return { kind: 'unknown', raw: value }
}

function loadPersistedQuery(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}

function persistQuery(key: string, value: string): void {
  try {
    if (value.length === 0) {
      window.localStorage.removeItem(key)
      return
    }
    if (value.length > MAX_PERSISTED_QUERY_LEN) return
    window.localStorage.setItem(key, value)
  } catch {
    // localStorage full / disabled — silent fail; persistence is a
    // convenience, not a correctness requirement.
  }
}

function loadRailWidth(): number {
  try {
    const stored = window.localStorage.getItem(RAIL_WIDTH_KEY)
    if (stored) {
      const parsed = Number.parseInt(stored, 10)
      if (Number.isFinite(parsed) && parsed >= RAIL_WIDTH_MIN && parsed <= RAIL_WIDTH_MAX) {
        return parsed
      }
    }
  } catch {
    // fall through
  }
  return RAIL_WIDTH_DEFAULT
}

function persistRailWidth(width: number): void {
  try {
    window.localStorage.setItem(RAIL_WIDTH_KEY, String(Math.round(width)))
  } catch {
    // best-effort
  }
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Unknown error.'
  const obj = error as Record<string, unknown>
  if (obj['data'] && typeof obj['data'] === 'object') {
    const data = obj['data'] as Record<string, unknown>
    if (typeof data['error'] === 'string') return data['error']
    if (typeof data['message'] === 'string') return data['message']
  }
  if (typeof obj['error'] === 'string') return obj['error']
  if (typeof obj['message'] === 'string') return obj['message']
  return 'Unknown error.'
}

// ISO-8601 timestamps come back from Postgres looking like
// `2026-05-20T22:52:00.875844Z`. That's exact but visually noisy in a
// table — drop the `T`/`Z` and clamp fractional seconds to millis so
// columns line up.
const ISO_TIMESTAMP_PATTERN = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})(\.\d+)?Z$/

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') {
    const match = ISO_TIMESTAMP_PATTERN.exec(value)
    if (match) {
      const fractional = match[3] ? match[3].slice(0, 4) : ''
      return `${match[1]} ${match[2]}${fractional}`
    }
    return value
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return safeStringify(value)
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}
